import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification, WoltModal } from 'common/components';
import { Typography as T } from 'common/typography';
import { useStopSharingMenuTracking } from 'modules/menu/hooks/telemetryHooks';
import { unlinkVenuesFromSharedMenu } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useSharedMenuData } from 'modules/shared-menu/hooks/useSharedMenuData';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedVenueIds: string[];
  sharedMenuId: string;
  allVenuesSelected: boolean;
  search: string;
};

const Root = styled(WoltModal)`
  width: 520px;
  border-radius: 8px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`;

const TextContainer = styled(T.Body3)`
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
`;

const ModalButtons = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

export const StopSharingWithVenuesModal = ({
  isOpen,
  onClose,
  selectedVenueIds,
  sharedMenuId,
  search,
  allVenuesSelected,
}: Props) => {
  const scope = useScopeContext();
  const { refetchSharedMenus } = useSharedMenuData({ scope });

  const { mutate, isLoading, error, reset } = useMutation<unknown, Error>(
    () => {
      if (!sharedMenuId) {
        return Promise.reject();
      }

      return unlinkVenuesFromSharedMenu(sharedMenuId, selectedVenueIds);
    },
    {
      onSuccess: async () => {
        await refetchSharedMenus();
        toast.success(<FormattedMessage id="shared-menu.unlink.success" />);
        handleClose();
      },
    },
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const trackStopSharingMenu = useStopSharingMenuTracking(allVenuesSelected, sharedMenuId, search, selectedVenueIds);

  return (
    <Root isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="shared-menu.unlink.modal.header" />
      </ModalHeader>
      <StyledModalContent>
        <TextContainer>
          <FormattedMessage id="shared-menu.unlink.modal.description-start" />
          <br /> <br />
          <FormattedMessage id="shared-menu.unlink.modal.description-end" />
        </TextContainer>
      </StyledModalContent>
      <StyledModalFooter>
        {error && <APIErrorNotification error={error} style={{ marginBlockEnd: '16px', width: '100%' }} />}
        <ModalButtons>
          <Button variant="lightBlue" size="small" style={{ marginInlineEnd: '8px' }} onClick={onClose}>
            <FormattedMessage id="state.cancel" />
          </Button>
          <Button
            variant="lightRed"
            size="small"
            disabled={isLoading}
            loading={isLoading}
            onClick={() => {
              mutate();
              trackStopSharingMenu();
            }}
          >
            <FormattedMessage id="shared-menu.unlink.button-label" />
          </Button>
        </ModalButtons>
      </StyledModalFooter>
    </Root>
  );
};
